import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Header } from '../components/Header';
import { Stepper } from '../components/Stepper';
import { AIOptionSelection } from '../components/AIOptionSelection';
import { AIModelSelection } from '../components/AIModelSelection';
import { SAIDetails } from '../components/SAIDetails';
import { Approach } from '../components/Approach';
import { Algorithm, MLAlgorithm } from '../components/Algorithm';
import { MLConfig } from '../components/MLConfig';
import { Training } from '../components/Training';
import { CustomModelResults } from '../components/CustomModelResults';
import { ExistingModelSelection } from '../components/ExistingModelSelection';
import { ExistingModelParameters } from '../components/ExistingModelParameters';
import { ParameterVisualization } from '../components/ParameterVisualization';
import { PredictionModelResults } from '../components/PredictionModelResults';
import { DesignModelResults } from '../components/DesignModelResults';
import { saiFilesApi, trainedModelsApi, chatApi } from '../services/api';
import { SAIFileResponse } from '../services/api';
import type { ChatSessionState } from '../services/api';
import type { WizardStep } from '../components/Stepper';
import type { AIOption } from '../components/AIOptionSelection';


type ModelMode = 'prediction' | 'design';

interface AIWizardState {
  step: WizardStep;
  selectedOption: AIOption | null;
  aiModel: string | null;
  selectedSAIFile: SAIFileResponse | null;
  approach: 'existing' | 'custom' | null;
  selectedAlgorithm: MLAlgorithm | null;
  selectedExistingModelId: string | null;
  selectedExistingModelName: string | null;
  selectedModelMode: ModelMode | null;
  mlConfig: any | null;
  trainingResult: any | null;
  predictionParameters: any[] | null;
  predictionInputValues: number[] | null; // Store input values for saving
  designResult: any | null;
  /** Saved design form state (values, optimize flags, constraints); used when returning via "Run New Analysis" */
  designParametersState: {
    designValues: Record<string, number | null>;
    optimizeFlags: Record<string, boolean>;
    constraintTypes: Record<string, string>;
    constraintValues: Record<string, string>;
  } | null;
  /** Last used ML config values; used when user clicks back or "reject and change parameter" */
  lastUsedMlConfig: any | null;
}

export const AIOptionWizard: React.FC<{
  sessionState: ChatSessionState;
  onSessionStateChange: (s: ChatSessionState) => void;
  navigateStep?: WizardStep | null;
  navigateResultData?: any;
  onNavigateHandled?: () => void;
}> = ({ sessionState, onSessionStateChange, navigateStep, navigateResultData, onNavigateHandled }) => {
  const [isTrainingInProgress, setIsTrainingInProgress] = useState(false);
  const [isOperationInProgress, setIsOperationInProgress] = useState(false);

  const initialWizardState: AIWizardState = {
    step: 'ai-option',
    selectedOption: null,
    aiModel: null,
    selectedSAIFile: null,
    approach: null,
    selectedAlgorithm: null,
    selectedExistingModelId: null,
    selectedExistingModelName: null,
    selectedModelMode: null,
    mlConfig: null,
    trainingResult: null,
    predictionParameters: null,
    predictionInputValues: null,
    designResult: null,
    designParametersState: null,
    lastUsedMlConfig: null,
  };

  const [wizardState, setWizardState] = useState<AIWizardState>(initialWizardState);

  // Refs for values used inside effects that shouldn't trigger re-runs
  const sessionStateRef = useRef(sessionState);
  sessionStateRef.current = sessionState;
  const onSessionStateChangeRef = useRef(onSessionStateChange);
  onSessionStateChangeRef.current = onSessionStateChange;
  const wizardStateRef = useRef(wizardState);
  wizardStateRef.current = wizardState;
  const navigateResultDataRef = useRef(navigateResultData);
  navigateResultDataRef.current = navigateResultData;
  const onNavigateHandledRef = useRef(onNavigateHandled);
  onNavigateHandledRef.current = onNavigateHandled;

  useEffect(() => {
    if (sessionStateRef.current.current_page !== wizardState.step) {
      onSessionStateChangeRef.current({ ...sessionStateRef.current, current_page: wizardState.step });
    }
  }, [wizardState.step]);

  useEffect(() => {
    if (navigateStep) {
      const updates: Partial<AIWizardState> = { step: navigateStep };
      const ss = sessionStateRef.current;
      const ws = wizardStateRef.current;
      const resultData = navigateResultDataRef.current;

      // Sync model selection from session state
      if (ss.model_index !== null && !ws.selectedExistingModelId) {
        updates.selectedExistingModelId = String(ss.model_index);
        updates.selectedExistingModelName = ss.model_name;
      }

      // Populate result data from chat response
      if (resultData) {
        if (resultData.mode) {
          updates.selectedModelMode = resultData.mode as ModelMode;
        }
        if (resultData.algorithm) {
          updates.selectedAlgorithm = resultData.algorithm as MLAlgorithm;
        }
        if (resultData.prediction_result) {
          updates.selectedModelMode = 'prediction';
          const fc = resultData.prediction_result;
          if (ws.selectedSAIFile?.parameters) {
            const params = ws.selectedSAIFile.parameters.map((p: any, i: number) => ({
              ...p,
              value: p.type?.toLowerCase() === 'output'
                ? fc.output?.[0]
                : fc.input?.[i],
            }));
            updates.predictionParameters = params;
            updates.predictionInputValues = fc.input;
          }
        }
        if (resultData.design_result) {
          updates.selectedModelMode = 'design';
          updates.designResult = resultData.design_result;
        }
        if (resultData.training_result) {
          updates.trainingResult = resultData.training_result;
        }
      }

      // Re-fetch file when navigating via chat to pick up any backend changes
      if (ss.sai_file_id) {
        saiFilesApi.getDetail(ss.sai_file_id).then((file) => {
          setWizardState(prev => ({ ...prev, ...updates, selectedSAIFile: file }));
        }).catch(() => {
          setWizardState(prev => ({ ...prev, ...updates }));
        });
      } else {
        setWizardState(prev => ({ ...prev, ...updates }));
      }
      onNavigateHandledRef.current?.();
    }
  }, [navigateStep]);

  const syncToChat = useCallback((action: string, detail: any, contextOverrides: Record<string, any> = {}) => {
    const ctx = {
      step: wizardState.step,
      approach: wizardState.approach,
      selected_algorithm: wizardState.selectedAlgorithm,
      selected_model_mode: wizardState.selectedModelMode,
      ...contextOverrides,
    };
    chatApi.sync({
      wizard_context: ctx,
      recent_actions: [{ action, detail, timestamp: new Date().toISOString() }],
      ...(contextOverrides.sai_file_id ? { sai_file_id: contextOverrides.sai_file_id } : {}),
      ...(contextOverrides.model_index !== undefined ? { model_index: contextOverrides.model_index } : {}),
    }).catch(() => {}); // fire-and-forget
  }, [wizardState]);

  const handleResetWizard = () => {
    setWizardState(initialWizardState);
    onSessionStateChange({
      sai_file_id: null,
      sai_file_name: null,
      model_index: null,
      model_name: null,
      current_page: 'ai-option',
      approach: null,
      selected_algorithm: null,
      selected_model_mode: null,
    });
    syncToChat('reset_wizard', {}, { step: 'ai-option', approach: null, selected_algorithm: null, selected_model_mode: null });
  };

  const handleOptionSelect = (option: AIOption) => {
    setWizardState({
      ...wizardState,
      selectedOption: option,
      step: 'ai-model',
    });
    syncToChat('selected_option', { option }, { step: 'ai-model' });
  };

  const handleBackToOptions = () => {
    setWizardState({
      ...wizardState,
      step: 'ai-option',
    });
  };

  const handleNextFromModel = (selectedFile: SAIFileResponse) => {
    setWizardState({
      ...wizardState,
      selectedSAIFile: selectedFile,
      step: 'sai-details',
    });
    onSessionStateChange({
      ...sessionState,
      sai_file_id: selectedFile.id,
      sai_file_name: selectedFile.filename,
    });
    syncToChat('selected_file', { id: selectedFile.id, filename: selectedFile.filename }, { step: 'sai-details', sai_file_id: selectedFile.id });
  };

  const handleBackToModel = () => {
    setWizardState({
      ...wizardState,
      step: 'ai-model',
    });
  };

  const handleNextFromSAIDetails = () => {
    setWizardState({
      ...wizardState,
      step: 'approach',
    });
    syncToChat('viewed_sai_details', {}, { step: 'approach' });
  };

  const handleBackToSAIDetails = () => {
    setWizardState({
      ...wizardState,
      step: 'sai-details',
    });
  };

  const handleSAIFileUpdate = (updatedFile: SAIFileResponse) => {
    setWizardState({
      ...wizardState,
      selectedSAIFile: updatedFile,
    });
  };

  const handleSelectExistingModel = () => {
    // Navigate to existing model selection
    console.log('Use existing model selected');
    setWizardState({
      ...wizardState,
      approach: 'existing',
      step: 'existing-model',
    });
    syncToChat('chose_existing_approach', {}, { step: 'existing-model', approach: 'existing' });
  };

  const handleTrainCustomModel = () => {
    // Navigate to custom model training - Algorithm step
    setWizardState({
      ...wizardState,
      approach: 'custom',
      step: 'algorithm',
    });
    syncToChat('chose_custom_approach', {}, { step: 'algorithm', approach: 'custom' });
  };

  const handleBackToApproach = () => {
    setWizardState({
      ...wizardState,
      step: 'approach',
      approach: null,
    });
  };

  const handleAlgorithmNext = (algorithm: MLAlgorithm) => {
    setWizardState({
      ...wizardState,
      selectedAlgorithm: algorithm,
      step: 'ml-config',
    });
    syncToChat('selected_algorithm', { algorithm }, { step: 'ml-config', selected_algorithm: algorithm });
  };

  const handleBackToAlgorithm = () => {
    setWizardState({
      ...wizardState,
      step: 'algorithm',
    });
  };

  const handleMLConfigNext = (data: any) => {
    setWizardState({
      ...wizardState,
      mlConfig: data.config,
      lastUsedMlConfig: data.config, // Save last used config
      step: 'training',
    });
    syncToChat('configured_ml', { config: data.config }, { step: 'training' });
  };

  const handleBackToMLConfig = () => {
    setWizardState({
      ...wizardState,
      step: 'ml-config',
    });
  };

  const handleViewResults = (trainingResult?: any) => {
    setWizardState({
      ...wizardState,
      trainingResult: trainingResult || null,
      step: 'results',
    });
    syncToChat('training_complete', { metrics: trainingResult?.test_metric }, { step: 'results' });
  };

  const handleRejectTryDifferent = () => {
    console.log('Reject model - try different algorithm');
    setWizardState({
      ...wizardState,
      step: 'algorithm',
    });
    syncToChat('rejected_model_try_different', {}, { step: 'algorithm' });
  };

  const handleRejectAdjustParams = () => {
    console.log('Reject model - adjust parameters');
    setWizardState({
      ...wizardState,
      step: 'ml-config',
    });
    syncToChat('rejected_model_adjust_params', {}, { step: 'ml-config' });
  };

  const handleAcceptModel = async (modelName: string, remarks: string) => {
    console.log('Accept model - saving and returning to home');

    if (!wizardState.selectedSAIFile?.id) {
      alert('Error: No SAI file selected');
      return;
    }

    if (!wizardState.trainingResult) {
      alert('Error: No training result available');
      return;
    }

    try {
      await saiFilesApi.saveModel(wizardState.selectedSAIFile.id, {
        name: modelName,
        remarks: remarks,
        binary_model: wizardState.trainingResult.binary_model,
        binary_scaler: wizardState.trainingResult.binary_scaler,
        test_metric: wizardState.trainingResult.test_metric,
        train_metric: wizardState.trainingResult.train_metric,
        additional_params: wizardState.trainingResult.additional_params
      });
    } catch (error) {
      console.error('Failed to save model:', error);
      alert(`Failed to save model: ${error}`);
      return;
    }

    // Navigate to home
    setWizardState({
      ...wizardState,
      step: 'ai-option',
    });
    syncToChat('accepted_model', { name: modelName }, { step: 'ai-option' });
  };

  // Existing model flow handlers
  const handleExistingModelNext = (modelId: string, modelName: string) => {
    console.log('Selected existing model:', modelId, modelName);
    setWizardState({
      ...wizardState,
      selectedExistingModelId: modelId,
      selectedExistingModelName: modelName,
      step: 'existing-parameters',
    });
    onSessionStateChange({
      ...sessionState,
      model_index: parseInt(modelId),
      model_name: modelName,
    });
    syncToChat('selected_model', { id: modelId, name: modelName }, { step: 'existing-parameters', model_index: parseInt(modelId) });
  };

  const handleBackToExistingModel = () => {
    setWizardState({
      ...wizardState,
      step: 'existing-model',
    });
  };

  const handleExistingParametersNext = (
    mode: ModelMode,
    paramResult?: any,
    designState?: {
      designValues: Record<string, number | null>;
      optimizeFlags: Record<string, boolean>;
      constraintTypes: Record<string, string>;
      constraintValues: Record<string, string>;
    },
    inputValues?: number[]
  ) => {
    setWizardState({
      ...wizardState,
      selectedModelMode: mode,
      predictionParameters: mode === 'prediction' ? (paramResult || null) : null,
      predictionInputValues: mode === 'prediction' ? (inputValues || null) : null,
      designResult: mode === 'design' ? (paramResult || null) : null,
      designParametersState: mode === 'design' && designState ? designState : (wizardState.designParametersState ?? null),
      step: 'existing-results',
    });
    syncToChat(mode === 'prediction' ? 'ran_prediction' : 'ran_design', { mode }, { step: 'existing-results', selected_model_mode: mode });
  };

  const handleSavePredictionResult = async () => {
    if (!wizardState.selectedExistingModelId || !wizardState.predictionInputValues || !wizardState.predictionParameters) {
      console.error('Missing required data for saving prediction');
      alert('Unable to save prediction: missing required data');
      return;
    }

    try {
      // Extract output value from prediction parameters
      const outputParam = wizardState.predictionParameters.find(p => p.type?.toLowerCase() === 'output');
      if (!outputParam) {
        throw new Error('No output parameter found in prediction results');
      }

      const outputValue = outputParam.value;
      
      await trainedModelsApi.savePrediction(
        parseInt(wizardState.selectedExistingModelId),
        wizardState.predictionInputValues,
        [outputValue]
      );

      alert('Prediction result saved successfully!');
      syncToChat('saved_prediction', {}, {});
    } catch (error: any) {
      console.error('Error saving prediction:', error);
      alert(`Failed to save prediction: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleStepClick = (step: WizardStep) => {
    // Allow navigation back to previous steps
    setWizardState({
      ...wizardState,
      step: step,
    });
  };

  const renderStepContent = () => {
    switch (wizardState.step) {
      case 'ai-option':
        return <AIOptionSelection onOptionSelect={handleOptionSelect} />;

      case 'ai-model':
        return (
          <AIModelSelection
            onBack={handleBackToOptions}
            onNext={handleNextFromModel}
          />
        );

      case 'sai-details':
        return wizardState.selectedSAIFile ? (
          <SAIDetails
            key={wizardState.selectedSAIFile.id}
            selectedFile={wizardState.selectedSAIFile}
            onBack={handleBackToModel}
            onNext={handleNextFromSAIDetails}
            onFileUpdate={handleSAIFileUpdate}
          />
        ) : null;

      case 'approach':
        return (
          <Approach
            onBack={handleBackToSAIDetails}
            onSelectExistingModel={handleSelectExistingModel}
            onTrainCustomModel={handleTrainCustomModel}
          />
        );

      case 'algorithm':
        return (
          <Algorithm
            onBack={handleBackToApproach}
            onNext={handleAlgorithmNext}
          />
        );

      case 'ml-config':
        return (
          <MLConfig
            algorithm={wizardState.selectedAlgorithm || 'random forest'}
            saiFileId={wizardState.selectedSAIFile?.id}
            lastUsedConfig={wizardState.lastUsedMlConfig}
            onBack={handleBackToAlgorithm}
            onNext={handleMLConfigNext}
          />
        );

      case 'training':
        return (
          <Training
            algorithm={wizardState.selectedAlgorithm || 'random forest'}
            saiFileId={wizardState.selectedSAIFile?.id}
            config={wizardState.mlConfig}
            onBack={handleBackToMLConfig}
            onViewResults={handleViewResults}
            onTrainingInProgress={setIsTrainingInProgress}
          />
        );

      case 'results':
        return (
          <CustomModelResults
            trainingResult={wizardState.trainingResult}
            selectedSAIFile={wizardState.selectedSAIFile}
            onBack={handleBackToMLConfig}
            onRejectTryDifferent={handleRejectTryDifferent}
            onRejectAdjustParams={handleRejectAdjustParams}
            onAcceptModel={handleAcceptModel}
          />
        );

      case 'existing-model':
        return wizardState.selectedSAIFile ? (
          <ExistingModelSelection
            selectedSAIFile={wizardState.selectedSAIFile}
            onBack={handleBackToApproach}
            onNext={handleExistingModelNext}
          />
        ) : null;

      case 'existing-parameters':
        return wizardState.selectedSAIFile ? (
          <ExistingModelParameters
            selectedSAIFile={wizardState.selectedSAIFile}
            modelName={wizardState.selectedExistingModelName || 'Model'}
            initialMode={wizardState.selectedModelMode || 'prediction'}
            initialPredictionParameters={wizardState.predictionParameters}
            initialDesignState={wizardState.designParametersState}
            onBack={handleBackToExistingModel}
            onNext={handleExistingParametersNext}
            onVisualize={() => setWizardState({ ...wizardState, step: 'visualize' })}
            onOperationInProgress={setIsOperationInProgress}
          />
        ) : null;

      case 'existing-results':
        if (wizardState.selectedModelMode === 'design') {
          return (
            <DesignModelResults
              selectedSAIFile={wizardState.selectedSAIFile!}
              modelName={wizardState.selectedExistingModelName || 'Model'}
              designResult={wizardState.designResult}
              onBack={() => setWizardState({ ...wizardState, step: 'existing-parameters' })}
              onRunNewAnalysis={() => setWizardState({ ...wizardState, step: 'existing-parameters' })}
            />
          );
        } else {
          return (
            <PredictionModelResults
              modelName={wizardState.selectedExistingModelName || 'Model'}
              selectedSAIFile={wizardState.selectedSAIFile}
              parameters={wizardState.predictionParameters || []}
              onBack={() => setWizardState({ ...wizardState, step: 'existing-parameters' })}
              onVerify={() => console.log('Verify clicked')}
              onGetNewPrediction={() => setWizardState({ ...wizardState, step: 'existing-parameters' })}
              onSavePredictionResult={handleSavePredictionResult}
            />
          );
        }

      case 'visualize':
        return wizardState.selectedSAIFile ? (
          <ParameterVisualization
            selectedSAIFile={wizardState.selectedSAIFile}
            modelName={wizardState.selectedExistingModelName || 'Model'}
            onBack={() => setWizardState({ ...wizardState, step: 'existing-parameters' })}
          />
        ) : null;

      default:
        return null;
    }
  };

  return (
    <div className="ai-wizard-container">
      <Header 
        onResetWizard={handleResetWizard} 
        saiFileName={wizardState.selectedSAIFile?.filename || null}
      />

      <main className="ai-wizard-main">
        <Stepper
          currentStep={wizardState.step}
          isCustomModelFlow={wizardState.approach === 'custom'}
          isExistingModelFlow={wizardState.approach === 'existing'}
          onStepClick={handleStepClick}
          disableStepClick={(wizardState.step === 'training' && isTrainingInProgress) || (wizardState.step === 'existing-parameters' && isOperationInProgress)}
        />

        {renderStepContent()}
      </main>
    </div>
  );
};
